import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2, RendererFactory2, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { AlertController, Platform } from '@ionic/angular/standalone';
import { Capacitor, Plugins } from '@capacitor/core';
import { CheckUpdateService } from './services/check-update.service';
import { UsersService } from './services/users.service';
import { ScreenOrientation } from '@awesome-cordova-plugins/screen-orientation/ngx';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { FirebaseMessaging } from '@awesome-cordova-plugins/firebase-messaging/ngx';
import { DOCUMENT } from '@angular/common';
import { NotificationService } from './services/notification.service';
import { StorageKeys, StorageService } from './services/storage.service';
import { TranslateService } from '@ngx-translate/core';
import { Globalization } from '@awesome-cordova-plugins/globalization/ngx';
import { environment } from 'src/environments/environment';
import { addIcons } from 'ionicons';
import { Configuration } from './configuration';
import { Utility } from './helpers/Utility';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { SignalRService } from './services/signalr.service';
import { assertNotNullOrUndefined } from './app-core/_core/common/guards';
import { ContextService, UnauthenticatedContext } from './services/context.service';
import { AdminContext } from './app-admin/admin-context';
import { ClientContext } from './app-clients/client-context';
import { FeatureFlags } from '../domain/feature-flags';
import { EnvironmentEnum } from 'src/domain/environment.enum';
import { NetworkStatusService } from './services/network-status.service';
import { ToastService } from './services/toast.service';
import * as ionicons from 'ionicons/icons';
import { Subject, takeUntil } from 'rxjs';

addIcons({
	collaborator: 'assets/svg/collaborator.svg',
	'collaborator-star': 'assets/svg/collaborator-star.svg',
	calendar1: 'assets/svg/calendar1.svg',
	calendar7: 'assets/svg/calendar7.svg',
	'icon-10min': 'assets/svg/icon-10min.svg',
	'icon-15min': 'assets/svg/icon-15min.svg',
	'icon-20min': 'assets/svg/icon-20min.svg',
	'icon-30min': 'assets/svg/icon-30min.svg',
});

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [Globalization],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
	public environment = environment;
	private renderer2: Renderer2;
	private offlineToast: any;

	fadeLoader = signal(false);
	private destroy$ = new Subject<void>();

	private isOfflineAlertCreating = false;

	constructor(
		private platform: Platform,
		private router: Router,
		private fcm: FirebaseMessaging,
		public usersService: UsersService,
		public checkUpdateService: CheckUpdateService,
		private statusBar: StatusBar,
		private screenOrientation: ScreenOrientation,
		private googleAnalyticsService: GoogleAnalyticsService,
		private alertCtrl: AlertController,
		private rendererFactory2: RendererFactory2,
		private notificationService: NotificationService,
		private storageService: StorageService,
		// private _permissionsService: PermissionsService,
		@Inject(DOCUMENT) private dom: Document,
		private translate: TranslateService,
		private globalization: Globalization,
		private contextService: ContextService,
		private readonly signalRService: SignalRService,
		private readonly _contextService: ContextService,
		private networkStatusService: NetworkStatusService,
		private toastService: ToastService,
	) {
		window['ENVIRONMENT'] = environment;
		this.renderer2 = rendererFactory2.createRenderer(null, null);
		this.initializeApp();
		// this.initializeFirebase();

		// Configuration.init().then();

		addIcons(icons);
	}

	// initializeFirebase() {
	// 	firebase.initializeApp(environment.firebaseConfig);
	// }

	initializeApp() {
		this.platform.ready().then(() => {
			if (Capacitor.getPlatform() !== 'web') {
				this.statusBar.backgroundColorByHexString('#ffffff');
				this.statusBar.overlaysWebView(false);
			}
			if (Capacitor.isPluginAvailable('SplashScreen')) {
				Plugins['SplashScreen']['hide']();
			}

			if (window.innerWidth < 768) {
				if (Capacitor.getPlatform() !== 'web') {
					this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT);
				} else {
					try {
						// @ts-ignore
						window.screen.orientation['lock']('portrait');
					} catch (err) {
						console.log(err);
					}
				}
			}
		});
	}

	ngOnInit() {
		sessionStorage.removeItem('hideWaAlert');
		console.log(`%cApp Version: ${environment.appVersion}`, 'color: green');

		this.translate.addLangs(['en', 'it']);

		this.router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.googleAnalyticsService.pageView(event.url);
			}
		});
		this.checkUpdateService.checkForUpdates();

		this.networkStatusService.isOnline$
			.pipe(takeUntil(this.destroy$))
			.subscribe(async (isOnline) => {
				if (!isOnline) {
					if (!this.offlineToast && !this.isOfflineAlertCreating) {
						this.isOfflineAlertCreating = true;
						try {
							const alert = await this.alertCtrl.create({
								header: 'Non sei connesso a internet!',
								message: 'Nessuna connessione internet sul dispositivo. Controlla la connessione!',
								cssClass: 'offline-alert',
								backdropDismiss: false,
								buttons: [{ text: 'Chiudi', role: 'cancel' }],
							});

							if (this.networkStatusService.getStatus()) {
								return;
							}

							this.offlineToast = alert;
							this.offlineToast.onDidDismiss().then(() => {
								this.offlineToast = null;
							});
							await this.offlineToast.present();

							if (this.networkStatusService.getStatus()) {
								await this.offlineToast?.dismiss();
								this.offlineToast = null;
							}
						} catch (error) {
							console.error('Error presenting offline alert', error);
						} finally {
							this.isOfflineAlertCreating = false;
						}
					}
				} else {
					if (this.offlineToast) {
						await this.offlineToast.dismiss();
						this.offlineToast = null;
						this.toastService.showToast('Connessione ripristinata', 'success');
					}
				}
			});

		if (this.contextService.isAuthenticated && !this.contextService.isUserCustomer) {
			this.requestNotification();
		}

		// this.storageService.get(StorageKeys.user).then((u) => {
		// 	let user: string | null = u;
		// 	if (!user) user = sessionStorage.getItem(StorageKeys.user);
		// 	if (user) {
		// 		this.usersService.getCurrentUserInfo().subscribe((user) => {
		// 			let id = user.LinguaDefaultApp;
		// 			const nameLang = Utility.languages.find((e) => e.Id === id);
		// 			assertNotNullOrUndefined(nameLang);
		// 			this.translate.setDefaultLang(nameLang.Nome.toLowerCase());
		// 			this.translate.use(nameLang.Nome.toLowerCase());
		// 			const permissionCodes = this.usersService.getPermissionCodes();
		// 			// this._permissionsService.setPermissions(permissionCodes);
		//
		// 			if (this.usersService.isCliente || this.usersService.isCollaboratore) {
		// 				const clientContext = this.usersService.createContext(user, permissionCodes);
		// 				this.contextService.setContext(clientContext);
		// 			}
		//
		// 			if (!this.usersService.isCustomer) {
		// 				this.requestNotification();
		// 			}
		// 			// this.appendManifest(user.id);
		// 			this.isContextInitialized = true;
		// 		});
		//
		// 		// this.signalRService.startConnection()
		// 		// .then(() => {
		// 		// 	this.signalRService.addMessageListener();
		// 		//
		// 		// 	this.signalRService.sendMessage("Test", "Test Message");
		// 		// })
		// 	} else {
		// 		this.translate.use('it');
		// 		// this.appendManifest();
		// 		if (
		// 			!location.href.includes('/reset-password') &&
		// 			!location.href.includes('/tos/') &&
		// 			!location.href.includes('/contact-us')
		// 		) {
		// 			this.router.navigateByUrl('/login');
		// 		}
		// 		this.isContextInitialized = true;
		// 	}
		// });
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			this.fadeLoader.set(true);
		}, 100);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private requestNotification() {
		if (Capacitor.getPlatform() !== 'web') {
			this.fcm.requestPermission().then((hasPermission) => {
				if (hasPermission) {
					this.fcm.getToken().then((token) => {
						// tslint:disable-next-line:no-console
						console.info('token', token);
						this.fcm.subscribe('marketingBusiness');
						this.usersService.sendInfo(token).subscribe();
					});

					this.fcm.onTokenRefresh().subscribe(() => {
						this.fcm.getToken().then((token) => {
							this.usersService.sendInfo(token).subscribe();
						});
					});

					this.fcm.onBackgroundMessage().subscribe((data) => { });
					this.fcm.onMessage().subscribe((data) => {
						this.alertCtrl
							.create({
								header: data.title,
								message: data.body,
							})
							.then((alert) => alert.present());
					});
				}
			});
		} else {
			this.usersService.sendInfo().subscribe();
		}
	}

	private appendManifest(idUser: number | null = null) {
		if (idUser != null) {
			const link = this.renderer2.createElement('link');
			link.href =
				Configuration.getString('wsApiUrl') +
				'/api/manifest/manifest/' +
				idUser +
				'/?url=' +
				encodeURIComponent(location.origin);
			link.rel = 'manifest';
			link.type = 'application/json';

			this.renderer2.appendChild(this.dom.head, link);
		} else {
			const link = this.renderer2.createElement('link');
			link.href =
				Configuration.getString('wsApiUrl') +
				'/api/manifest/manifest/?url=' +
				encodeURIComponent(location.origin);
			link.rel = 'manifest';
			link.type = 'application/json';

			this.renderer2.appendChild(this.dom.head, link);
		}
	}

	private formatLanguage(language: string) {
		return language.split('-')[0].toLowerCase();
	}
}

const icons = {
	...ionicons
}
